import { Component, Input, OnInit } from '@angular/core';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import { NzMessageService } from 'ng-zorro-antd/message';
import * as g from 'src/app/globals';
import { Line } from '../interfaces/cartObject';
import { ShoppingProducts } from '../interfaces/ShoppingProductsItem';

interface VariantOption {
  Title: string;
  Name: string;
  Unique?: string;
}

interface CustomVariantRow {
  Quantity: number;
  [key: string]: string | number;
}

@Component({
  selector: 'app-custom-variant',
  templateUrl: './custom-variant.component.html',
  styleUrls: ['./custom-variant.component.less'],
})
export class CustomVariantComponent implements OnInit {
  @Input() product: ShoppingProducts;
  @Input() item: Line;

  global = g;
  variantOptions: VariantOption[] = [];
  rows: CustomVariantRow[] = [];

  trackByIndex = (i: number) => i;

  constructor(
    private message: NzMessageService,
    private drawerRef: NzDrawerRef
  ) {}

  ngOnInit(): void {
    this.variantOptions = this.parseVariantOptions(this.global.sc_VariantOptions);
    this.loadSavedVariants();
  }

  addRow(): void {
    const row: CustomVariantRow = { Quantity: 1 };
    this.variantOptions.forEach((option) => {
      row[option.Name] = '';
    });
    this.rows = [...this.rows, row];
  }

  deleteRow(index: number): void {
    this.rows.splice(index, 1);
    this.rows = [...this.rows];
  }

  canSubmit(): boolean {
    return !this.rows.some((row) => !this.isValidQuantity(row.Quantity));
  }

  onSubmit(): void {
    const rows = this.rows.map((row) => this.normalizeRow(row));

    if (!this.validateRows(rows)) {
      return;
    }

    if (!this.validateUniqueFields(rows)) {
      return;
    }

    const totalQuantity = rows.reduce(
      (sum, row) => sum + Number(row.Quantity || 0),
      0
    );

    this.product.CustomVariant = JSON.stringify(rows);
    this.product.SelectedQuantity = totalQuantity;
    this.product.Quantity = totalQuantity;

    this.drawerRef.close(rows);
  }

  private loadSavedVariants(): void {
    const savedValue =
      this.product?.CustomVariant ||
      this.item?.CustomVariant ||
      this.item?.productLine?.CustomVariant ||
      '';

    if (!savedValue) {
      this.rows = [];
      return;
    }

    try {
      const parsed = JSON.parse(savedValue);
      this.rows = Array.isArray(parsed)
        ? parsed.map((row) => this.normalizeRow(row))
        : [];
    } catch (error) {
      console.warn('Unable to parse product custom variants.', error);
      this.rows = [];
      this.message.warning('Saved custom variants could not be loaded.');
    }
  }

  private parseVariantOptions(value: string): VariantOption[] {
    if (!value) {
      return [];
    }

    try {
      const parsed = JSON.parse(value);
      if (!Array.isArray(parsed)) {
        console.warn('SC_VariantOptions must be a JSON array.');
        return [];
      }

      return parsed
        .filter(
          (option) =>
            option != null &&
            typeof option.Title === 'string' &&
            option.Title.trim() !== '' &&
            typeof option.Name === 'string' &&
            option.Name.trim() !== ''
        )
        .map((option) => ({
          Title: option.Title,
          Name: option.Name,
          Unique: option.Unique,
        }));
    } catch (error) {
      console.warn('Unable to parse SC_VariantOptions.', error);
      this.message.warning('Custom variant options are not configured correctly.');
      return [];
    }
  }

  private normalizeRow(row: any): CustomVariantRow {
    const normalizedRow: CustomVariantRow = {
      Quantity: Number(row?.Quantity || 0),
    };

    this.variantOptions.forEach((option) => {
      normalizedRow[option.Name] =
        row?.[option.Name] == null ? '' : row[option.Name];
    });

    return normalizedRow;
  }

  private isValidQuantity(quantity: number): boolean {
    return quantity != null && quantity !== undefined && Number(quantity) > 0;
  }

  private validateRows(rows: CustomVariantRow[]): boolean {
    for (const row of rows) {
      if (!this.isValidQuantity(row.Quantity)) {
        this.message.error('Quantity is required and must be greater than 0.');
        return false;
      }

      for (const option of this.variantOptions) {
        const value = row[option.Name];
        if (value == null || value.toString().trim() === '') {
          this.message.error(`${option.Title} is required.`);
          return false;
        }
      }
    }

    return true;
  }

  private validateUniqueFields(rows: CustomVariantRow[]): boolean {
    const uniqueOptions = this.variantOptions.filter(
      (option) => option.Unique?.trim().toLowerCase() === 'yes'
    );

    for (const option of uniqueOptions) {
      const values = new Set<string>();

      for (const row of rows) {
        const value = (row[option.Name] || '').toString().trim().toLowerCase();
        if (value === '') {
          continue;
        }

        if (values.has(value)) {
          this.message.error(`${option.Title} must be unique.`);
          return false;
        }

        values.add(value);
      }
    }

    return true;
  }
}
